import { isApiError } from '../api/client';
import type { DiscoveredPerson, RoomInvite, SocialPerson } from '../api/social';

// The Friends hub (H10-S6b): the home-screen overlay where the follow graph is grown
// and invites are answered. Four strips, in the order a returning player needs them:
//   INVITES     — join (the ordinary ?room=CODE path) or dismiss; joining also dismisses,
//                 because a tray entry for the room you are now in is noise (D84 keeps
//                 the row server-side either way).
//   FOLLOW BACK — people who follow you that you do not follow back; one tap makes a
//                 friendship (Instagram semantics, the user's chosen model).
//   FIND        — search by name (multiple matches by design — names are not unique,
//                 T-NET-031) and the people you recently PLAYED with, which is the
//                 natural, consent-shaped way to follow someone after a match.
//   FRIENDS     — the mutual list, with unfollow (which ends it for both, D81).
// ui/ consumes a structural port; navigation is a callback — the hub never touches
// window.location itself (T-UI-020 spirit).

export interface FriendsHubPort {
  invites(): Promise<RoomInvite[]>;
  dismissInvite(id: number): Promise<void>;
  followBacks(): Promise<SocialPerson[]>;
  friends(): Promise<SocialPerson[]>;
  follow(accountId: string): Promise<void>;
  unfollow(accountId: string): Promise<void>;
  search(query: string): Promise<DiscoveredPerson[]>;
  recentPlayers(): Promise<DiscoveredPerson[]>;
}

export interface FriendsHubOpts {
  doc: Document;
  onJoin(roomCode: string): void;
  onClose(): void;
}

export interface FriendsHub {
  readonly el: HTMLElement;
  /** Re-fetch everything (called on open). */
  refresh(): Promise<void>;
  dispose(): void;
}

export function mountFriendsHub(host: HTMLElement, port: FriendsHubPort, opts: FriendsHubOpts): FriendsHub {
  const doc = opts.doc;
  const el = doc.createElement('div');
  el.className = 'friends-hub';
  el.dataset['friendsHub'] = '1';
  host.appendChild(el);
  let disposed = false;

  const caps = (text: string): HTMLElement => {
    const l = doc.createElement('div');
    l.className = 'caps-label';
    l.textContent = text;
    return l;
  };
  const note = (text: string): HTMLElement => {
    const n = doc.createElement('div');
    n.className = 'social-note';
    n.textContent = text;
    return n;
  };
  const pill = (label: string, hook: string, id: string, onClick: (btn: HTMLButtonElement) => void): HTMLButtonElement => {
    const b = doc.createElement('button');
    b.className = 'social-invite';
    b.dataset[hook] = id;
    b.textContent = label;
    b.addEventListener('click', () => onClick(b));
    return b;
  };
  const fail = (e: unknown): string => (isApiError(e) ? e.message : 'network error');

  const head = doc.createElement('div');
  head.className = 'friends-hub-head';
  const title = doc.createElement('div');
  title.className = 'modal-title';
  title.textContent = 'Friends';
  const close = doc.createElement('button');
  close.className = 'modal-close';
  close.dataset['friendsClose'] = '1';
  close.textContent = '✕';
  close.setAttribute('aria-label', 'Close');
  close.addEventListener('click', () => opts.onClose());
  head.append(title, close);

  const body = doc.createElement('div');
  body.className = 'friends-hub-body';
  const errorEl = doc.createElement('div');
  errorEl.className = 'social-error';
  errorEl.dataset['friendsError'] = '1';
  el.append(head, body, errorEl);
  const showError = (text: string): void => {
    errorEl.textContent = text;
  };

  const inviteRow = (inv: RoomInvite): HTMLElement => {
    const r = doc.createElement('div');
    r.className = 'social-row';
    r.dataset['inviteRow'] = String(inv.id);
    const name = doc.createElement('div');
    name.className = 'social-name';
    name.textContent = `${inv.fromDisplayName} · room ${inv.roomCode}`;
    const join = pill('Join', 'inviteJoin', String(inv.id), () => {
      void port.dismissInvite(inv.id).catch(() => undefined); // joined ⇒ the tray row is noise
      opts.onJoin(inv.roomCode);
    });
    const dismiss = pill('Dismiss', 'inviteDismiss', String(inv.id), (btn) => {
      btn.disabled = true;
      port
        .dismissInvite(inv.id)
        .then(() => {
          if (!disposed) r.remove();
        })
        .catch((e: unknown) => {
          if (disposed) return;
          btn.disabled = false;
          showError(fail(e));
        });
    });
    r.append(name, join, dismiss);
    return r;
  };

  const personRow = (p: SocialPerson, action: HTMLElement | null): HTMLElement => {
    const r = doc.createElement('div');
    r.className = 'social-row';
    r.dataset['socialRow'] = p.id;
    const name = doc.createElement('div');
    name.className = 'social-name';
    name.textContent = p.displayName;
    r.appendChild(name);
    if (action) r.appendChild(action);
    return r;
  };

  const followButton = (p: DiscoveredPerson): HTMLButtonElement => {
    // one button, three honest states: Follow → (them following you already) friendship,
    // otherwise a plain follow; Following → unfollow on tap (ends a friendship, D81)
    const label = p.following ? 'Following ✓' : p.followsYou ? 'Follow back' : 'Follow';
    return pill(label, 'followToggle', p.id, (btn) => {
      btn.disabled = true;
      const flip = p.following ? port.unfollow(p.id) : port.follow(p.id);
      flip
        .then(() => {
          if (disposed) return;
          p.following = !p.following;
          btn.textContent = p.following ? 'Following ✓' : p.followsYou ? 'Follow back' : 'Follow';
          btn.disabled = false;
        })
        .catch((e: unknown) => {
          if (disposed) return;
          btn.disabled = false;
          showError(fail(e));
        });
    });
  };

  const searchStrip = (): HTMLElement => {
    const wrap = doc.createElement('div');
    wrap.className = 'social-section';
    wrap.appendChild(caps('FIND PLAYERS'));
    const rowEl = doc.createElement('div');
    rowEl.className = 'social-search';
    const input = doc.createElement('input');
    input.type = 'text';
    input.placeholder = 'search by name…';
    input.maxLength = 20;
    input.dataset['friendsSearch'] = '1';
    const results = doc.createElement('div');
    results.dataset['friendsResults'] = '1';
    const go = pill('Search', 'friendsSearchGo', '1', (btn) => {
      const q = input.value.trim();
      if (q.length < 2) {
        showError('type at least 2 characters');
        return;
      }
      btn.disabled = true;
      port
        .search(q)
        .then((rows) => {
          if (disposed) return;
          btn.disabled = false;
          results.textContent = '';
          if (rows.length === 0) results.appendChild(note('nobody by that name'));
          for (const p of rows) results.appendChild(personRow(p, followButton(p)));
        })
        .catch((e: unknown) => {
          if (disposed) return;
          btn.disabled = false;
          showError(fail(e));
        });
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') go.click();
    });
    rowEl.append(input, go);
    wrap.append(rowEl, results);
    return wrap;
  };

  const refresh = async (): Promise<void> => {
    body.textContent = '';
    errorEl.textContent = '';
    body.appendChild(note('loading…'));
    try {
      const [invites, followBacks, friends, recent] = await Promise.all([
        port.invites(),
        port.followBacks(),
        port.friends(),
        port.recentPlayers(),
      ]);
      if (disposed) return;
      body.textContent = '';

      const inv = doc.createElement('div');
      inv.className = 'social-section';
      inv.appendChild(caps('INVITES'));
      if (invites.length === 0) inv.appendChild(note('no invites waiting'));
      for (const i of invites) inv.appendChild(inviteRow(i));
      body.appendChild(inv);

      const fb = doc.createElement('div');
      fb.className = 'social-section';
      fb.appendChild(caps('FOLLOWS YOU'));
      if (followBacks.length === 0) fb.appendChild(note('nobody new'));
      for (const p of followBacks) {
        fb.appendChild(personRow(p, followButton({ ...p, following: false, followsYou: true })));
      }
      body.appendChild(fb);

      body.appendChild(searchStrip());

      const recentWrap = doc.createElement('div');
      recentWrap.className = 'social-section';
      recentWrap.appendChild(caps('RECENTLY PLAYED WITH'));
      if (recent.length === 0) recentWrap.appendChild(note('play a game — your opponents land here'));
      for (const p of recent) recentWrap.appendChild(personRow(p, followButton(p)));
      body.appendChild(recentWrap);

      const fr = doc.createElement('div');
      fr.className = 'social-section';
      fr.appendChild(caps('FRIENDS'));
      if (friends.length === 0) fr.appendChild(note('no friends yet — mutual follows land here'));
      for (const p of friends) {
        fr.appendChild(personRow(p, followButton({ ...p, following: true, followsYou: true })));
      }
      body.appendChild(fr);
    } catch (e: unknown) {
      if (disposed) return;
      body.textContent = '';
      body.appendChild(note(fail(e)));
    }
  };

  return {
    el,
    refresh,
    dispose(): void {
      disposed = true;
      el.remove();
    },
  };
}
